import {
  CalendarDayData,
  CalendarDayRoleGroupData,
  CalendarViewData,
  ComputeCalendarViewDataInput,
  DotKind,
  PlanWeekData,
  PlanWeekRoleGroupEntry,
  PlanWeekEntriesByCpfId,
  PlanEntriesByWeek,
} from './types';
import { grayDotDaysInRange } from './grayDotPlacement';
import {
  createSiteCalendarClock,
  SiteCalendarClock,
} from './siteCalendarClock';
import {
  compareDateKeys,
  parseStartOfWeekIndex,
  formatVisitFrequencyLabel,
  roleGroupAbbreviation,
} from './utils';

// ─── Constants ───────────────────────────────────────────────────────────────

/** JS Date.getDay() index for each abbreviation used in daysOfWeek strings. */
const DAY_ABBREV_INDEX: Record<string, number> = {
  SUN: 0,
  M: 1,
  T: 2,
  W: 3,
  THU: 4,
  F: 5,
  SAT: 6,
};

/**
 * CPF period names and how each recurs. Mirrors
 * `@mummssoftware/utils/frequencies/frequency-constants` (kept local so this
 * data module has no runtime dependency on the utils build).
 *
 * `Bi-Weekly` / `Bi-Monthly` are retired (no longer selectable in CPVF or
 * Orders and Progress Notes) but rows saved with them still schedule once
 * every two weeks / two months.
 */
export const DAILY_PERIOD = 'Daily';
export const WEEKLY_PERIOD = 'Weekly';
export const BI_WEEKLY_PERIOD = 'Bi-Weekly';
export const MONTHLY_PERIOD = 'Monthly';
export const BI_MONTHLY_PERIOD = 'Bi-Monthly';

/** Period length a schedule actually recurs on. */
type CadencePeriod =
  | typeof DAILY_PERIOD
  | typeof WEEKLY_PERIOD
  | typeof BI_WEEKLY_PERIOD
  | typeof MONTHLY_PERIOD
  | typeof BI_MONTHLY_PERIOD;

type Cadence = {
  /** Recurrence length. */
  period: CadencePeriod;
  /** Visits expected in each period. */
  frequency: number;
  /** Human label for dots / modals, e.g. "2x Weekly" or "1x Bi-Weekly". */
  label: string;
};

/** Normalise a CPF row's period + frequency into a recurrence cadence. */
export const cadenceForCpf = (
  period: string,
  frequency: number | null | undefined,
): Cadence | null => {
  if (!frequency) return null;
  switch (period) {
    case DAILY_PERIOD:
    case WEEKLY_PERIOD:
    case BI_WEEKLY_PERIOD:
    case MONTHLY_PERIOD:
    case BI_MONTHLY_PERIOD:
      return {
        period,
        frequency,
        label: formatVisitFrequencyLabel(frequency, period),
      };
    default:
      return null;
  }
};

/**
 * CPF "1 X Assess" rows are saved with period `Assess` (frequency 1): a single
 * one-time visit, not a recurring schedule.
 */
export const ASSESS_PERIOD = 'Assess';

/**
 * pimreader visit status for a visit that is checked in but not yet completed.
 * Mirrors `InProgress` in @mummssoftware/utils/constants; kept local so this
 * data module has no runtime dependency on the utils build.
 */
export const IN_PROGRESS_VISIT_STATUS = 'In Progress';

// ─── Internal types ───────────────────────────────────────────────────────────

/**
 * A single unit of planned visits for one role group, anchored to a CPF row's
 * effectiveDate and covering one "period" (day / week / 2 weeks / month / 2 months).
 */
type AdminPeriod = {
  cpfId: number;
  roleGroupId: number;
  discipline: string;
  /** Visits expected in this period. */
  frequency: number;
  /** Recurrence length this period was generated on. */
  period: CadencePeriod | typeof ASSESS_PERIOD;
  frequencyText: string;
  /** Display label for the row's frequency, e.g. "3x Weekly" or "Bi-Monthly". */
  label: string;
  /** Day-of-week indices (0=Sun … 6=Sat) parsed from daysOfWeek. */
  specificDayIndices: number[];
  /** Inclusive start (site-timezone midnight). */
  start: Date;
  /** Exclusive end (first day of next period, site-timezone midnight). */
  end: Date;
  /** Ended early because a superseding CPF row took over mid-period. */
  interrupted: boolean;
};

type ScoredPeriod = AdminPeriod & {
  completed: number;
  missed: number;
  isPast: boolean;
  isCurrent: boolean;
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

const advancePeriod = (
  clock: SiteCalendarClock,
  date: Date,
  period: string,
): Date | null => {
  switch (period) {
    case DAILY_PERIOD:
      return clock.addDays(date, 1);
    case WEEKLY_PERIOD:
      return clock.addDays(date, 7);
    case BI_WEEKLY_PERIOD:
      return clock.addDays(date, 14);
    case MONTHLY_PERIOD:
      return clock.addCalendarMonths(date, 1);
    case BI_MONTHLY_PERIOD:
      return clock.addCalendarMonths(date, 2);
    default:
      return null;
  }
};

/** Collect every Date in [start, end). */
const datesInRange = (
  clock: SiteCalendarClock,
  start: Date,
  end: Date,
): Date[] => {
  const dates: Date[] = [];
  let cur = new Date(start);
  while (cur < end) {
    dates.push(new Date(cur));
    cur = clock.addDays(cur, 1);
  }
  return dates;
};

/** Return the earlier of two nullable dates, falling back to whichever is non-null. */
const earlierDate = (a: Date | null, b: Date | null): Date | null => {
  if (a && b) return a < b ? a : b;
  return a ?? b;
};

// ─── CPF grouping & governing-range logic ────────────────────────────────────

/** Group CPF rows by roleGroupId. */
const groupByRoleGroup = (
  cpfRows: mumms.patientCarePlanProblemFrequency[],
): Map<number, mumms.patientCarePlanProblemFrequency[]> =>
  cpfRows.reduce((map, row) => {
    const arr = map.get(row.roleGroupId) ?? [];
    arr.push(row);
    map.set(row.roleGroupId, arr);
    return map;
  }, new Map<number, mumms.patientCarePlanProblemFrequency[]>());

type GoverningRange = {
  cpf: mumms.patientCarePlanProblemFrequency;
  govStart: Date;
  /**
   * Exclusive end of governance: the effectiveDate of the next superseding row,
   * or null if open-ended. Rows with the same effectiveDate and higher id
   * immediately supersede (govEnd === govStart → zero periods generated).
   */
  govEnd: Date | null;
};

/**
 * Sort CPF rows by effectiveDate ASC then id ASC (highest-id wins ties) and
 * assign governance ranges (a provider may have multiple frequencies active over a date
 * range, work out which ones govern which chunks of time)
 */
const toGoverningRanges = (
  cpfRows: mumms.patientCarePlanProblemFrequency[],
  clock: SiteCalendarClock,
): GoverningRange[] => {
  const sorted = [...cpfRows].sort((a, b) => {
    const dateDiff =
      clock.parseCpfDate(a.effectiveDate).getTime() -
      clock.parseCpfDate(b.effectiveDate).getTime();
    return dateDiff !== 0 ? dateDiff : a.id - b.id;
  });

  return sorted.map((cpf, i) => ({
    cpf,
    govStart: clock.parseCpfDate(cpf.effectiveDate),
    govEnd:
      i + 1 < sorted.length
        ? clock.parseCpfDate(sorted[i + 1].effectiveDate)
        : null,
  }));
};

const parseSpecificDayIndices = (
  daysOfWeek: string | null | undefined,
): number[] => {
  if (!daysOfWeek) return [];
  return daysOfWeek
    .split(',')
    .map((abbr) => DAY_ABBREV_INDEX[abbr.trim()])
    .filter((idx): idx is number => idx !== undefined);
};

// ─── Admin period generation ──────────────────────────────────────────────────

/**
 * Advance date forward to the next occurrence of weekStartDayIndex (0=Sun…6=Sat).
 * If date is already on that day, return it unchanged.
 */
const snapToWeekStart = (
  date: Date,
  weekStartDayIndex: number,
  clock: SiteCalendarClock,
): Date => {
  const dayOfWeek = clock.dayOfWeek(date);
  const daysUntil = (weekStartDayIndex - dayOfWeek + 7) % 7;
  return daysUntil === 0 ? date : clock.addDays(date, daysUntil);
};

/** Calendar-month periods drift off week boundaries; align ends to week-start. */
const CALENDAR_MONTH_PERIODS = new Set<string>([
  MONTHLY_PERIOD,
  BI_MONTHLY_PERIOD,
]);

const alignMonthlyPeriodEnd = (
  naturalEnd: Date,
  weekStartDayIndex: number,
  clock: SiteCalendarClock,
): Date => snapToWeekStart(naturalEnd, weekStartDayIndex, clock);

/** Clip to governance/row end; flag periods cut short by supersession (not row endDate alone). */
const clipPeriodEnd = (
  naturalPeriodEnd: Date,
  effectiveGovEnd: Date | null,
  govEnd: Date | null,
): { periodEnd: Date; interrupted: boolean } => {
  if (!effectiveGovEnd || naturalPeriodEnd <= effectiveGovEnd) {
    return { periodEnd: naturalPeriodEnd, interrupted: false };
  }

  const periodEnd = effectiveGovEnd;
  const interrupted =
    govEnd != null &&
    periodEnd.getTime() === govEnd.getTime() &&
    naturalPeriodEnd > govEnd;

  return { periodEnd, interrupted };
};

const MS_DAY = 86400000;

/** True when admin period [start, end) overlaps the visible calendar range. */
const periodOverlapsVisibleRange = (
  start: Date,
  end: Date,
  visibleStart: Date,
  visibleEnd: Date,
): boolean => start < visibleEnd && end > visibleStart;

/**
 * Jump periodStart forward without materializing skipped periods.
 * Fixed-length periods use arithmetic; calendar-month periods step by period.
 */
const fastForwardPeriodStart = (
  periodStart: Date,
  period: string,
  visibleStart: Date,
  weekStartDayIndex: number,
  clock: SiteCalendarClock,
): Date => {
  if (periodStart >= visibleStart) {
    return periodStart;
  }

  if (CALENDAR_MONTH_PERIODS.has(period)) {
    let cursor = periodStart;
    for (let i = 0; i < 10000; i += 1) {
      const naturalEnd = advancePeriod(clock, cursor, period);
      if (!naturalEnd) {
        return cursor;
      }

      const periodEnd = alignMonthlyPeriodEnd(
        naturalEnd,
        weekStartDayIndex,
        clock,
      );
      if (periodEnd <= cursor) {
        return cursor;
      }
      if (periodEnd > visibleStart) {
        return cursor;
      }
      cursor = periodEnd;
    }

    return cursor;
  }

  const elapsedMs = visibleStart.getTime() - periodStart.getTime();
  if (elapsedMs <= 0) {
    return periodStart;
  }

  switch (period) {
    case DAILY_PERIOD: {
      const days = Math.floor(elapsedMs / MS_DAY);
      return days > 0 ? clock.addDays(periodStart, days) : periodStart;
    }
    case WEEKLY_PERIOD: {
      const weeks = Math.floor(elapsedMs / (7 * MS_DAY));
      return weeks > 0 ? clock.addDays(periodStart, weeks * 7) : periodStart;
    }
    case BI_WEEKLY_PERIOD: {
      const biWeeks = Math.floor(elapsedMs / (14 * MS_DAY));
      return biWeeks > 0
        ? clock.addDays(periodStart, biWeeks * 14)
        : periodStart;
    }
    default:
      break;
  }

  let cursor = periodStart;
  for (let i = 0; i < 10000; i += 1) {
    const naturalEnd = advancePeriod(clock, cursor, period);
    if (!naturalEnd || naturalEnd >= visibleStart) {
      return cursor;
    }
    cursor = naturalEnd;
  }

  return cursor;
};

/**
 * A "to assess" row yields exactly one admin period: from the effective date
 * through the end of the week (per startOfWeek) that contains it. It is clipped
 * like any other period when a later row supersedes it.
 */
const assessPeriodForGoverningRange = (
  { cpf, govStart, govEnd }: GoverningRange,
  weekStartDayIndex: number,
  clock: SiteCalendarClock,
): AdminPeriod[] => {
  const cpfEndDate = cpf.endDate ? clock.parseCpfDate(cpf.endDate) : null;
  const effectiveGovEnd = earlierDate(govEnd, cpfEndDate);

  const start = new Date(govStart);
  const daysSinceWeekStart =
    (clock.dayOfWeek(start) - weekStartDayIndex + 7) % 7;
  const weekStart = clock.addDays(start, -daysSinceWeekStart);
  const naturalEnd = clock.addDays(weekStart, 7);
  const { periodEnd, interrupted } = clipPeriodEnd(
    naturalEnd,
    effectiveGovEnd,
    govEnd,
  );

  if (periodEnd <= start) return [];

  return [
    {
      cpfId: cpf.id,
      roleGroupId: cpf.roleGroupId,
      discipline: cpf.discipline,
      frequency: cpf.frequency || 1,
      period: ASSESS_PERIOD,
      frequencyText: cpf.frequencyText,
      label: formatVisitFrequencyLabel(cpf.frequency || 1, ASSESS_PERIOD),
      specificDayIndices: parseSpecificDayIndices(cpf.daysOfWeek),
      start,
      end: periodEnd,
      interrupted,
    },
  ];
};

/**
 * Generate admin periods for govRange that overlap the visible calendar window.
 * Skips historical periods before visibleStart without iterating them one-by-one.
 */
const adminPeriodsForGoverningRange = (
  { cpf, govStart, govEnd }: GoverningRange,
  weekStartDayIndex: number,
  clock: SiteCalendarClock,
  visibleStart: Date,
  visibleEnd: Date,
): AdminPeriod[] => {
  if (cpf.period === ASSESS_PERIOD) {
    return assessPeriodForGoverningRange(
      { cpf, govStart, govEnd },
      weekStartDayIndex,
      clock,
    ).filter((p) =>
      periodOverlapsVisibleRange(p.start, p.end, visibleStart, visibleEnd),
    );
  }

  const cadence = cadenceForCpf(cpf.period, cpf.frequency);
  if (!cadence) return [];

  const specificDayIndices = parseSpecificDayIndices(cpf.daysOfWeek);
  const cpfEndDate = cpf.endDate ? clock.parseCpfDate(cpf.endDate) : null;
  const effectiveGovEnd = earlierDate(govEnd, cpfEndDate); // @note

  if (effectiveGovEnd && effectiveGovEnd <= visibleStart) {
    return [];
  }

  let periodStart = snapToWeekStart(
    new Date(govStart),
    weekStartDayIndex,
    clock,
  );
  periodStart = fastForwardPeriodStart(
    periodStart,
    cadence.period,
    visibleStart,
    weekStartDayIndex,
    clock,
  );

  if (periodStart >= visibleEnd) {
    return [];
  }

  const periods: AdminPeriod[] = [];

  while (effectiveGovEnd === null || periodStart < effectiveGovEnd) {
    if (periodStart >= visibleEnd) {
      break;
    }

    const naturalEnd = advancePeriod(clock, periodStart, cadence.period);
    if (!naturalEnd) break;

    const naturalPeriodEnd = CALENDAR_MONTH_PERIODS.has(cadence.period)
      ? alignMonthlyPeriodEnd(naturalEnd, weekStartDayIndex, clock)
      : naturalEnd;
    const { periodEnd, interrupted } = clipPeriodEnd(
      naturalPeriodEnd,
      effectiveGovEnd,
      govEnd,
    );

    if (periodEnd <= periodStart) break;

    if (
      periodOverlapsVisibleRange(
        periodStart,
        periodEnd,
        visibleStart,
        visibleEnd,
      )
    ) {
      periods.push({
        cpfId: cpf.id,
        roleGroupId: cpf.roleGroupId,
        discipline: cpf.discipline,
        frequency: cadence.frequency,
        period: cadence.period,
        frequencyText: cpf.frequencyText,
        label: cadence.label,
        specificDayIndices,
        start: new Date(periodStart),
        end: new Date(periodEnd),
        interrupted,
      });
    }

    periodStart = periodEnd;
  }

  return periods;
};

/**
 * Pre-optimization period generation: walk every admin period from governance
 * start through end. Used only to verify visible-range clipping does not change
 * in-view output.
 */
const adminPeriodsForGoverningRangeUnbounded = (
  { cpf, govStart, govEnd }: GoverningRange,
  weekStartDayIndex: number,
  clock: SiteCalendarClock,
): AdminPeriod[] => {
  if (cpf.period === ASSESS_PERIOD) {
    return assessPeriodForGoverningRange(
      { cpf, govStart, govEnd },
      weekStartDayIndex,
      clock,
    );
  }

  const cadence = cadenceForCpf(cpf.period, cpf.frequency);
  if (!cadence) return [];

  const specificDayIndices = parseSpecificDayIndices(cpf.daysOfWeek);
  const cpfEndDate = cpf.endDate ? clock.parseCpfDate(cpf.endDate) : null;
  const effectiveGovEnd = earlierDate(govEnd, cpfEndDate);

  const periods: AdminPeriod[] = [];
  let periodStart = snapToWeekStart(
    new Date(govStart),
    weekStartDayIndex,
    clock,
  );

  while (effectiveGovEnd === null || periodStart < effectiveGovEnd) {
    const naturalEnd = advancePeriod(clock, periodStart, cadence.period);
    if (!naturalEnd) break;

    const naturalPeriodEnd = CALENDAR_MONTH_PERIODS.has(cadence.period)
      ? alignMonthlyPeriodEnd(naturalEnd, weekStartDayIndex, clock)
      : naturalEnd;
    const { periodEnd, interrupted } = clipPeriodEnd(
      naturalPeriodEnd,
      effectiveGovEnd,
      govEnd,
    );

    if (periodEnd <= periodStart) break;

    periods.push({
      cpfId: cpf.id,
      roleGroupId: cpf.roleGroupId,
      discipline: cpf.discipline,
      frequency: cadence.frequency,
      period: cadence.period,
      frequencyText: cpf.frequencyText,
      label: cadence.label,
      specificDayIndices,
      start: new Date(periodStart),
      end: new Date(periodEnd),
      interrupted,
    });

    periodStart = periodEnd;

    if (periods.length > 10000) break;
  }

  return periods;
};

const filterPeriodsToVisibleRange = (
  periods: ScoredPeriod[],
  visibleStart: Date,
  visibleEnd: Date,
  clock: SiteCalendarClock,
): ScoredPeriod[] =>
  periods.filter((p) => {
    const lastDay = clock.addDays(p.end, -1);
    const overlapsRange = p.start < visibleEnd && p.end > visibleStart;
    const lastDayInRange = lastDay >= visibleStart && lastDay < visibleEnd;
    return overlapsRange || lastDayInRange;
  });

export type PeriodGenerationMode = 'visible-clipped' | 'full-history';

export type ComputeCalendarViewDataOptions = {
  /** Default `visible-clipped` (optimized). `full-history` matches pre-optimization behavior. */
  periodGeneration?: PeriodGenerationMode;
};

const scorePeriod = (
  period: AdminPeriod,
  visitsByRoleGroup: Map<number, mumms.pimreaderVisit[]>,
  clock: SiteCalendarClock,
  today: Date,
): ScoredPeriod => {
  const roleGroupVisits = visitsByRoleGroup.get(period.roleGroupId) ?? [];
  const completed = roleGroupVisits.filter((v) => {
    const visitDate = clock.parseVisitDate(v);
    if (!visitDate) return false;
    return visitDate >= period.start && visitDate < period.end;
  }).length;

  const missed = Math.max(0, period.frequency - completed); // @note
  const todayKey = clock.dateKey(today);
  const periodEndKey = clock.dateKey(period.end);
  const isPast = compareDateKeys(todayKey, periodEndKey) >= 0;
  const isCurrent =
    compareDateKeys(todayKey, clock.dateKey(period.start)) >= 0 &&
    compareDateKeys(todayKey, periodEndKey) < 0;

  return { ...period, completed, missed, isPast, isCurrent };
};

// ─── Gray dot placement ───────────────────────────────────────────────────────

const grayDotDays = (
  period: AdminPeriod,
  from: Date,
  count: number,
  clock: SiteCalendarClock,
): Date[] => {
  if (count <= 0) return [];

  const effectiveFrom = from > period.start ? from : period.start;
  const available = datesInRange(clock, effectiveFrom, period.end);
  if (available.length === 0) return [];

  if (count >= available.length) return available;

  return grayDotDaysInRange(
    available,
    period.specificDayIndices,
    count,
    (day) => clock.dayOfWeek(day),
  );
};

type PlanCounts = { missed: number; todo: number; planned: number };

/**
 * Plan-column counts follow period-level frequency scoring, not per-planned-day
 * matching. A visit on any day within the admin period satisfies obligation until
 * the period ends; only then is the shortfall truly missed.
 *
 * Superseded (interrupted) periods never get a red missed box. When past, they
 * follow the same rule as normal periods: show nothing (no plan box).
 */
const getPlanMissedAndTodo = (period: ScoredPeriod): PlanCounts => {
  const planned = period.frequency;

  if (period.interrupted) {
    if (period.isPast) {
      return { missed: 0, todo: 0, planned: 0 };
    }

    return { missed: 0, todo: period.missed, planned };
  }

  if (period.isPast) {
    return { missed: period.missed, todo: 0, planned };
  }

  return { missed: 0, todo: period.missed, planned };
};

/**
 * Week-row key (per startOfWeek) that hosts a period's plan box: always the
 * week containing the period's last day, so a reader can see when an
 * obligation is due (todo) or where it ended (missed) with one rule.
 */
const planWeekKeyForPeriod = (
  period: ScoredPeriod,
  weekStartDayIndex: number,
  clock: SiteCalendarClock,
): string =>
  clock.weekStartDateKey(clock.addDays(period.end, -1), weekStartDayIndex);

// ─── Day / week data builders ────────────────────────────────────────────────

const ensureDay = (
  days: Record<string, CalendarDayData>,
  dateKey: string,
): CalendarDayData => {
  if (!days[dateKey]) {
    // eslint-disable-next-line no-param-reassign
    days[dateKey] = { dateKey, roleGroups: [] };
  }
  return days[dateKey];
};

const visitDotKey = (
  roleGroupId: number,
  visit: mumms.pimreaderVisit,
): string => `visit::${roleGroupId}::${visit.id}`;

const grayDotKey = (roleGroupId: number): string => `gray::${roleGroupId}`;

/** In-progress visits render yellow; everything else recorded renders green. */
const visitDotKind = (visit: mumms.pimreaderVisit): DotKind =>
  visit.status === IN_PROGRESS_VISIT_STATUS ? 'yellow' : 'green';

/** One dot per visit; replaces any gray dot for the same role group. */
const addVisitDot = (
  days: Record<string, CalendarDayData>,
  dateKey: string,
  entry: CalendarDayRoleGroupData,
): void => {
  const day = ensureDay(days, dateKey);
  day.roleGroups = day.roleGroups.filter(
    (r) => !(r.dotKind === 'gray' && r.roleGroupId === entry.roleGroupId),
  );

  if (day.roleGroups.some((r) => r.dotKey === entry.dotKey)) {
    return;
  }

  day.roleGroups.push(entry);
};

/** One gray dot per role group per day; skipped when a visit dot exists for that role. */
const upsertGrayDot = (
  days: Record<string, CalendarDayData>,
  dateKey: string,
  entry: CalendarDayRoleGroupData,
): void => {
  const day = ensureDay(days, dateKey);

  if (
    day.roleGroups.some(
      (r) => r.dotKind !== 'gray' && r.roleGroupId === entry.roleGroupId,
    )
  ) {
    return;
  }

  const idx = day.roleGroups.findIndex((r) => r.dotKey === entry.dotKey);
  if (idx === -1) {
    day.roleGroups.push(entry);
  }
};

const mergePlanEntry = (
  existing: PlanWeekRoleGroupEntry,
  period: ScoredPeriod,
  lastDay: Date,
  counts: PlanCounts,
  clock: SiteCalendarClock,
): void => {
  existing.missed += counts.missed;
  existing.todo += counts.todo;
  existing.planned += counts.planned;
  if (period.interrupted) {
    existing.interrupted = true;
  }
  const periodStartKey = clock.dateKey(period.start);
  const periodEndKey = clock.dateKey(lastDay);
  if (compareDateKeys(periodStartKey, existing.periodStart) < 0) {
    existing.periodStart = periodStartKey;
  }
  if (compareDateKeys(periodEndKey, existing.periodEnd) > 0) {
    existing.periodEnd = periodEndKey;
  }
  existing.periods.push({ start: periodStartKey, end: periodEndKey });
};

const isInVisibleRangeInclusive = (
  date: Date,
  visibleStart: Date,
  visibleEnd: Date,
): boolean => date >= visibleStart && date <= visibleEnd;

const newPlanWeekRoleGroupEntry = (
  period: ScoredPeriod,
  lastDay: Date,
  counts: PlanCounts,
  roleAbbrev: string,
  clock: SiteCalendarClock,
): PlanWeekRoleGroupEntry => ({
  cpfId: period.cpfId,
  roleGroupId: String(period.roleGroupId),
  roleAbbrev,
  missed: counts.missed,
  todo: counts.todo,
  planned: counts.planned,
  interrupted: period.interrupted || undefined,
  periodStart: clock.dateKey(period.start),
  periodEnd: clock.dateKey(lastDay),
  periods: [
    { start: clock.dateKey(period.start), end: clock.dateKey(lastDay) },
  ],
});

const upsertPlanWeekEntry = (
  byWeek: PlanEntriesByWeek,
  period: ScoredPeriod,
  lastDay: Date,
  counts: PlanCounts,
  roleAbbrev: string,
  weekStartDayIndex: number,
  clock: SiteCalendarClock,
): void => {
  const weekKey = clock.weekStartDateKey(lastDay, weekStartDayIndex);
  const weekMap = byWeek.get(weekKey) ?? (new Map() as PlanWeekEntriesByCpfId);
  const existing = weekMap.get(period.cpfId);

  if (existing) {
    mergePlanEntry(existing, period, lastDay, counts, clock);
  } else {
    weekMap.set(
      period.cpfId,
      newPlanWeekRoleGroupEntry(period, lastDay, counts, roleAbbrev, clock),
    );
  }

  byWeek.set(weekKey, weekMap);
};

const buildPlanEntriesByWeek = (
  periods: ScoredPeriod[],
  visibleStart: Date,
  visibleEnd: Date,
  weekStartDayIndex: number,
  roleAbbrev: (roleGroupId: number, fallback: string) => string,
  clock: SiteCalendarClock,
): PlanEntriesByWeek => {
  // Satisfied periods still count toward "of y" (planned) when several periods
  // collapse into one week box, so they are merged first and a box is dropped
  // only when nothing in it is missed or still to do.
  const byWeek = periods.reduce((acc, period) => {
    const counts = getPlanMissedAndTodo(period);
    if (counts.planned <= 0) return acc;

    const lastDay = clock.addDays(period.end, -1);
    if (!isInVisibleRangeInclusive(lastDay, visibleStart, visibleEnd)) {
      return acc;
    }

    upsertPlanWeekEntry(
      acc,
      period,
      lastDay,
      counts,
      roleAbbrev(period.roleGroupId, period.discipline),
      weekStartDayIndex,
      clock,
    );
    return acc;
  }, new Map() as PlanEntriesByWeek);

  byWeek.forEach((weekMap, weekKey) => {
    weekMap.forEach((entry, cpfId) => {
      if (entry.missed <= 0 && entry.todo <= 0) weekMap.delete(cpfId);
    });
    if (weekMap.size === 0) byWeek.delete(weekKey);
  });

  return byWeek;
};

// ─── Main export ──────────────────────────────────────────────────────────────

/** One scored administration period, keyed to the plan-column week that hosts it. */
export type CompliancePeriod = {
  cpfId: number;
  roleGroupId: number;
  discipline: string;
  /** Visits expected in this period. */
  frequency: number;
  label: string;
  /** Non-padded date keys, end inclusive. */
  periodStart: string;
  periodEnd: string;
  /** Week row (per startOfWeek) that hosts the period's plan box. */
  planWeekKey: string;
  /** Visits counted against the plan (completed and in progress). */
  completed: number;
  /** Of `completed`, how many are still in progress. */
  inProgress: number;
  missed: number;
  todo: number;
  isPast: boolean;
  isCurrent: boolean;
  interrupted?: boolean;
};

type ScoringContext = {
  clock: SiteCalendarClock;
  weekStartDayIndex: number;
  visibleStart: Date;
  visibleEnd: Date;
  today: Date;
  visitsByRoleGroup: Map<number, mumms.pimreaderVisit[]>;
  visitsByKey: Map<string, mumms.pimreaderVisit[]>;
  relevantPeriods: ScoredPeriod[];
};

/** Index visits, generate the admin periods for the range and score them. */
const scoreRange = (
  input: ComputeCalendarViewDataInput,
  periodGeneration: PeriodGenerationMode,
): ScoringContext => {
  const {
    calendarDates,
    carePlanFrequencies,
    visits,
    startOfWeek,
    siteTimezone,
  } = input;

  const clock = createSiteCalendarClock(siteTimezone);
  const weekStartDayIndex = parseStartOfWeekIndex(startOfWeek);

  const visibleStart = clock.parseCalendarDate(calendarDates[0]);
  const visibleEnd = clock.parseCalendarDate(calendarDates[1]);
  const today = clock.todayStart();
  // const today = clock.addDays(clock.todayStart(), -3);

  // ── Index visits ──────────────────────────────────────────────────────────
  const visitsByRoleGroup = new Map<number, mumms.pimreaderVisit[]>();
  const visitsByKey = new Map<string, mumms.pimreaderVisit[]>();

  visits
    .filter((v) => v.rolegroup_id != null && clock.visitDateKey(v))
    .forEach((visit) => {
      const rgId = visit.rolegroup_id;
      const dateKey = clock.visitDateKey(visit)!;

      const rgArr = visitsByRoleGroup.get(rgId) ?? [];
      rgArr.push(visit);
      visitsByRoleGroup.set(rgId, rgArr);

      const key = `${rgId}::${dateKey}`;
      const keyArr = visitsByKey.get(key) ?? [];
      keyArr.push(visit);
      visitsByKey.set(key, keyArr); // key is (role group id, date)
    });

  // ── Generate and score admin periods ────────────────────────────────────────
  const cpfByRoleGroup = groupByRoleGroup(carePlanFrequencies);

  const scoreGovernancePeriods = (
    govRange: GoverningRange,
    generatePeriods: (range: GoverningRange) => AdminPeriod[],
  ): ScoredPeriod[] =>
    generatePeriods(govRange).map((period) =>
      scorePeriod(period, visitsByRoleGroup, clock, today),
    );

  const scoredPeriods: ScoredPeriod[] = Array.from(
    cpfByRoleGroup.values(),
  ).flatMap((cpfRows) =>
    toGoverningRanges(cpfRows, clock).flatMap((govRange) =>
      periodGeneration === 'full-history'
        ? scoreGovernancePeriods(govRange, (range) =>
            adminPeriodsForGoverningRangeUnbounded(
              range,
              weekStartDayIndex,
              clock,
            ),
          )
        : scoreGovernancePeriods(govRange, (range) =>
            adminPeriodsForGoverningRange(
              range,
              weekStartDayIndex,
              clock,
              visibleStart,
              visibleEnd,
            ),
          ),
    ),
  );

  const relevantPeriods =
    periodGeneration === 'full-history'
      ? filterPeriodsToVisibleRange(
          scoredPeriods,
          visibleStart,
          visibleEnd,
          clock,
        )
      : scoredPeriods;

  return {
    clock,
    weekStartDayIndex,
    visibleStart,
    visibleEnd,
    today,
    visitsByRoleGroup,
    visitsByKey,
    relevantPeriods,
  };
};

/**
 * OP-33263: the scored administration periods behind the plan column, for
 * callers that need planned vs actual counts per role group and week without
 * the calendar day data (the cross patient compliance table). Satisfied
 * periods are included too, unlike `CalendarViewData.weeks`, which only keeps
 * boxes that still have missed or todo visits.
 */
export const computeCompliancePeriods = (
  input: ComputeCalendarViewDataInput,
  options: ComputeCalendarViewDataOptions = {},
): CompliancePeriod[] => {
  const periodGeneration = options.periodGeneration ?? 'visible-clipped';
  const {
    clock,
    weekStartDayIndex,
    visitsByRoleGroup,
    relevantPeriods,
  } = scoreRange(input, periodGeneration);

  return relevantPeriods.map((period) => {
    const counts = getPlanMissedAndTodo(period);
    const inProgress = (visitsByRoleGroup.get(period.roleGroupId) ?? []).filter(
      (visit) => {
        if (visit.status !== IN_PROGRESS_VISIT_STATUS) return false;
        const visitDate = clock.parseVisitDate(visit);
        return (
          !!visitDate && visitDate >= period.start && visitDate < period.end
        );
      },
    ).length;
    return {
      cpfId: period.cpfId,
      roleGroupId: period.roleGroupId,
      discipline: period.discipline,
      frequency: period.frequency,
      label: period.label,
      periodStart: clock.dateKey(period.start),
      periodEnd: clock.dateKey(clock.addDays(period.end, -1)),
      planWeekKey: planWeekKeyForPeriod(period, weekStartDayIndex, clock),
      completed: period.completed,
      inProgress,
      missed: counts.missed,
      todo: counts.todo,
      isPast: period.isPast,
      isCurrent: period.isCurrent,
      interrupted: period.interrupted || undefined,
    };
  });
};

export const computeCalendarViewData = (
  input: ComputeCalendarViewDataInput,
  options: ComputeCalendarViewDataOptions = {},
): CalendarViewData => {
  const periodGeneration = options.periodGeneration ?? 'visible-clipped';
  const { calendarDates, roleGroups, siteTimezone } = input;
  const {
    clock,
    weekStartDayIndex,
    visibleStart,
    visibleEnd,
    today,
    visitsByRoleGroup,
    visitsByKey,
    relevantPeriods,
  } = scoreRange(input, periodGeneration);

  // ── Role group display helpers ──────────────────────────────────────────────
  const rgName = (roleGroupId: number, fallback: string): string => {
    const rg = roleGroups.find((r) => r.id === roleGroupId);
    return rg?.name ?? fallback;
  };

  const rgAbbrev = (roleGroupId: number, fallback: string): string =>
    roleGroupAbbreviation(rgName(roleGroupId, fallback));

  const days: Record<string, CalendarDayData> = {};
  const weeks: Record<string, PlanWeekData> = {};

  // ── Visit dots: recorded visits in the visible range (green / yellow) ─────
  visitsByRoleGroup.forEach((rgVisits, rgId) => {
    rgVisits
      .filter((visit) => {
        const visitDate = clock.parseVisitDate(visit);
        if (!visitDate) return false;
        return visitDate >= visibleStart && visitDate < visibleEnd;
      })
      .forEach((visit) => {
        const visitDate = clock.parseVisitDate(visit)!;
        const dateKey = clock.visitDateKey(visit)!;
        const abbrev = rgAbbrev(rgId, visit.rolegroupname);
        const name = rgName(rgId, visit.rolegroupname);

        const govPeriod = relevantPeriods.find(
          (p) =>
            p.roleGroupId === rgId && visitDate >= p.start && visitDate < p.end,
        );

        addVisitDot(days, dateKey, {
          dotKey: visitDotKey(rgId, visit),
          roleGroupId: String(rgId),
          roleAbbrev: abbrev,
          roleGroupName: name,
          dotKind: visitDotKind(visit),
          visits: [visit],
          frequencyLabel: govPeriod ? govPeriod.label : '',
          cpfId: govPeriod?.cpfId ?? 0,
          periodStart: govPeriod ? clock.dateKey(govPeriod.start) : dateKey,
          periodEnd: govPeriod
            ? clock.dateKey(clock.addDays(govPeriod.end, -1))
            : dateKey,
          planWeekKey: govPeriod
            ? planWeekKeyForPeriod(govPeriod, weekStartDayIndex, clock)
            : clock.weekStartDateKey(visitDate, weekStartDayIndex),
        });
      });
  });

  // ── Gray dots: planned future visits (current & future periods) ───────────
  relevantPeriods
    .filter((period) => !period.isPast && period.missed > 0)
    .forEach((period) => {
      const from = period.isCurrent ? today : period.start;
      const count = period.isCurrent ? period.missed : period.frequency;
      const abbrev = rgAbbrev(period.roleGroupId, period.discipline);
      const name = rgName(period.roleGroupId, period.discipline);

      grayDotDays(period, from, count, clock)
        .filter((grayDay) => grayDay >= visibleStart && grayDay <= visibleEnd)
        .filter(
          (grayDay) =>
            !visitsByKey.has(
              `${period.roleGroupId}::${clock.dateKey(grayDay)}`,
            ),
        )
        .forEach((grayDay) => {
          upsertGrayDot(days, clock.dateKey(grayDay), {
            dotKey: grayDotKey(period.roleGroupId),
            roleGroupId: String(period.roleGroupId),
            roleAbbrev: abbrev,
            roleGroupName: name,
            dotKind: 'gray',
            visits: [],
            frequencyLabel: period.label,
            cpfId: period.cpfId,
            periodStart: clock.dateKey(period.start),
            periodEnd: clock.dateKey(clock.addDays(period.end, -1)),
            planWeekKey: planWeekKeyForPeriod(period, weekStartDayIndex, clock),
          });
        });
    });

  // ── Missed column: collapse missed/todo counts by frequency (cpfId) per week
  const planEntriesByWeek = buildPlanEntriesByWeek(
    relevantPeriods,
    visibleStart,
    visibleEnd,
    weekStartDayIndex,
    rgAbbrev,
    clock,
  );

  planEntriesByWeek.forEach((weekMap, weekKey) => {
    weeks[weekKey] = {
      weekStartDateKey: weekKey,
      missedCounts: Array.from(weekMap.values()),
    };
  });

  return {
    siteTimezone,
    rangeStart: calendarDates[0],
    rangeEnd: calendarDates[1],
    days,
    weeks,
  };
};

const visitFingerprint = (visit: mumms.pimreaderVisit): string =>
  `${visit.rolegroup_id ?? ''}::${
    visit.starteventdatetime_shifted ?? visit.effectivedate ?? ''
  }`;

const normalizeRoleGroupDot = (entry: CalendarDayRoleGroupData) => ({
  dotKey: entry.dotKey,
  roleGroupId: entry.roleGroupId,
  roleAbbrev: entry.roleAbbrev,
  roleGroupName: entry.roleGroupName,
  dotKind: entry.dotKind,
  visitFingerprints: entry.visits.map(visitFingerprint).sort(),
  frequencyLabel: entry.frequencyLabel,
  cpfId: entry.cpfId,
  periodStart: entry.periodStart,
  periodEnd: entry.periodEnd,
  planWeekKey: entry.planWeekKey,
});

const normalizePlanEntry = (entry: PlanWeekRoleGroupEntry) => ({
  cpfId: entry.cpfId,
  roleGroupId: entry.roleGroupId,
  roleAbbrev: entry.roleAbbrev,
  missed: entry.missed,
  todo: entry.todo,
  planned: entry.planned,
  interrupted: entry.interrupted ?? false,
  periodStart: entry.periodStart,
  periodEnd: entry.periodEnd,
  periods: entry.periods.map((p) => ({ start: p.start, end: p.end })),
});

/** Stable JSON shape for parity checks (ignores visit object identity). */
export const normalizeCalendarViewDataForComparison = (
  data: CalendarViewData,
): unknown => ({
  siteTimezone: data.siteTimezone,
  rangeStart: data.rangeStart,
  rangeEnd: data.rangeEnd,
  days: Object.keys(data.days)
    .sort()
    .reduce((acc, dateKey) => {
      acc[dateKey] = {
        dateKey,
        roleGroups: [...data.days[dateKey].roleGroups]
          .sort((a, b) => a.dotKey.localeCompare(b.dotKey))
          .map(normalizeRoleGroupDot),
      };
      return acc;
    }, {} as Record<string, unknown>),
  weeks: Object.keys(data.weeks)
    .sort()
    .reduce((acc, weekKey) => {
      acc[weekKey] = {
        weekStartDateKey: data.weeks[weekKey].weekStartDateKey,
        missedCounts: [...data.weeks[weekKey].missedCounts]
          .sort((a, b) => a.cpfId - b.cpfId)
          .map(normalizePlanEntry),
      };
      return acc;
    }, {} as Record<string, unknown>),
});

export type CalendarViewDataParityResult =
  | { ok: true }
  | { ok: false; detail: string };

/** Compare optimized output to pre-optimization full-history + post-filter path. */
export const calendarViewDataMatchesReference = (
  reference: CalendarViewData,
  optimized: CalendarViewData,
): CalendarViewDataParityResult => {
  const referenceJson = JSON.stringify(
    normalizeCalendarViewDataForComparison(reference),
  );
  const optimizedJson = JSON.stringify(
    normalizeCalendarViewDataForComparison(optimized),
  );

  if (referenceJson === optimizedJson) {
    return { ok: true };
  }

  const ref = normalizeCalendarViewDataForComparison(reference) as {
    days: Record<string, { roleGroups: unknown[] }>;
    weeks: Record<string, { missedCounts: unknown[] }>;
  };
  const opt = normalizeCalendarViewDataForComparison(optimized) as {
    days: Record<string, { roleGroups: unknown[] }>;
    weeks: Record<string, { missedCounts: unknown[] }>;
  };

  const refDayKeys = Object.keys(ref.days).sort();
  const optDayKeys = Object.keys(opt.days).sort();
  if (JSON.stringify(refDayKeys) !== JSON.stringify(optDayKeys)) {
    return {
      ok: false,
      detail: `day keys differ (reference=${refDayKeys.length}, optimized=${optDayKeys.length})`,
    };
  }

  const differingDay = refDayKeys.find((dateKey) => {
    const refDots = JSON.stringify(ref.days[dateKey].roleGroups);
    const optDots = JSON.stringify(opt.days[dateKey].roleGroups);
    return refDots !== optDots;
  });
  if (differingDay) {
    return { ok: false, detail: `days[${differingDay}] roleGroups differ` };
  }

  const refWeekKeys = Object.keys(ref.weeks).sort();
  const optWeekKeys = Object.keys(opt.weeks).sort();
  if (JSON.stringify(refWeekKeys) !== JSON.stringify(optWeekKeys)) {
    return {
      ok: false,
      detail: `week keys differ (reference=${refWeekKeys.length}, optimized=${optWeekKeys.length})`,
    };
  }

  const differingWeek = refWeekKeys.find((weekKey) => {
    const refPlan = JSON.stringify(ref.weeks[weekKey].missedCounts);
    const optPlan = JSON.stringify(opt.weeks[weekKey].missedCounts);
    return refPlan !== optPlan;
  });
  if (differingWeek) {
    return { ok: false, detail: `weeks[${differingWeek}] plan entries differ` };
  }

  return { ok: false, detail: 'normalized payloads differ' };
};
